/**
 * CookieConsentBanner (F28 — FUTURE_ENHANCEMENTS).
 *
 * One-shot consent banner shown on first visit. Persists the user's choice
 * in localStorage so the next visit is silent. The banner doesn't actually
 * gate any feature — we only ever store essential session state
 * (auth token, theme, saved chart prefs) which is "strictly necessary" /
 * legitimate-interest under e-Privacy.  This is a transparency notice +
 * link to the privacy policy / settings; the "Decline" path still lets
 * the user in but flags their preference for any future analytics gate.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
// Hotfix — DO NOT import react-router-dom here. The banner mounts OUTSIDE the
// <Router>, so RouterLink would throw at render and blank-screen the entire
// app. Use a plain anchor; the legal section opens in a new tab anyway.

const STORAGE_KEY = 'cookie-consent-v1';

type Choice = 'accepted' | 'declined';

export const CookieConsentBanner: React.FC = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (!v) setOpen(true);
    } catch {
      // localStorage blocked (private mode / sandboxed iframe) → show the
      // banner; we just can't persist the choice, which is fine — they'll
      // see it next visit.
      setOpen(true);
    }
  }, []);

  const decide = (choice: Choice) => {
    try { localStorage.setItem(STORAGE_KEY, choice); } catch { /* unstorable */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Paper elevation={6} sx={{
      position: 'fixed',
      left: { xs: 8, sm: 16 },
      right: { xs: 8, sm: 16 },
      bottom: { xs: 8, sm: 16 },
      p: 2,
      zIndex: 1400,
      maxWidth: 720,
      mx: 'auto',
      borderLeft: '4px solid #1976d2',
      borderRadius: 1.5,
    }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Cookies & local storage
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.85rem', color: 'text.secondary' }}>
        Prashant uses strictly-necessary local storage for your authentication session, theme preference,
        and saved chart layouts. No advertising, no third-party analytics, no cross-site tracking. By using
        the app you agree to this minimum essential storage. See the{' '}
        <a
          href="/app-guide#legal"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1976d2', textDecoration: 'underline' }}
        >
          legal &amp; data section of the App Guide
        </a>{' '}
        for details.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button variant="text" size="small" onClick={() => decide('declined')}>
          Acknowledge (no analytics)
        </Button>
        <Button variant="contained" size="small" onClick={() => decide('accepted')}>
          Got it
        </Button>
      </Box>
    </Paper>
  );
};

export default CookieConsentBanner;
